import React, {useEffect, useMemo, useState} from 'react';
import styles from '../../styles/dashboard.module.css';
import Header from "../../components/header/header";
import PageTitle from "../../components/page_title/page_title";
import axiosInstance from "../../AxiosInstance";
import {BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid} from "recharts";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";

const COLORS = ['#4F46E5', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];

export default function ProductionComparison() {
    const {t} = useTranslation();
    const projectId = localStorage.getItem('project_id');
    const [allDevices, setAllDevices] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [expandedMachines, setExpandedMachines] = useState({});
    const [search, setSearch] = useState('');
    const [period, setPeriod] = useState('7d');
    const [chartType, setChartType] = useState('bar');
    const [shifts, setShifts] = useState([]);
    const [selectedShift, setSelectedShift] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [goodSeries, setGoodSeries] = useState([]);
    const [badSeries, setBadSeries] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let isMounted = true;
        Promise.all([
            axiosInstance.get(`device/?project_id=${projectId}`),
            axiosInstance.get(`device_user/?project_id=${projectId}`),
        ]).then(([devRes, machRes]) => {
            if (!isMounted) return;
            const machineMap = {};
            (machRes.data || []).filter(m => !m.is_human).forEach(m => { machineMap[m.id] = m.name; });
            // Only include devices that belong to a machine (exclude person devices)
            const devs = (devRes.data || [])
                .filter(d => machineMap[d.device_user])
                .map(d => ({...d, machineName: machineMap[d.device_user]}));
            setAllDevices(devs);
            // Expand all machines by default
            const expanded = {};
            Object.keys(machineMap).forEach(id => { expanded[id] = true; });
            setExpandedMachines(expanded);
        });
        return () => { isMounted = false; };
    }, [projectId]);

    // Fetch shifts when devices are selected (use shifts from first selected device)
    useEffect(() => {
        if (selectedDevices.length > 0) {
            let isMounted = true;
            axiosInstance.get(`production_shift/?device_id=${selectedDevices[0]}`).then(res => {
                if (isMounted) setShifts(Array.isArray(res.data) ? res.data : (res.data?.results || []));
            });
            return () => { isMounted = false; };
        } else {
            setShifts([]);
            setSelectedShift(null);
            if (period === 'by_shift') setPeriod('7d');
        }
    }, [selectedDevices]);

    // Group devices by machine
    const machineGroups = useMemo(() => {
        const map = {};
        allDevices.forEach(d => {
            const key = d.device_user;
            if (!map[key]) map[key] = {id: d.device_user, name: d.machineName, devices: []};
            map[key].devices.push(d);
        });
        return Object.values(map);
    }, [allDevices]);

    // Filter by search
    const filteredGroups = useMemo(() => {
        if (!search.trim()) return machineGroups;
        const q = search.toLowerCase();
        return machineGroups.map(g => ({
            ...g,
            devices: g.devices.filter(d =>
                d.name.toLowerCase().includes(q) || g.name.toLowerCase().includes(q)
            )
        })).filter(g => g.devices.length > 0);
    }, [machineGroups, search]);

    const toggleMachine = (machineId) => {
        setExpandedMachines(prev => ({...prev, [machineId]: !prev[machineId]}));
    };

    const toggleDevice = (deviceId) => {
        setSelectedDevices(prev =>
            prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
        );
    };

    const toggleAllInMachine = (machineDevices) => {
        const ids = machineDevices.map(d => d.id);
        const allSelected = ids.every(id => selectedDevices.includes(id));
        if (allSelected) {
            setSelectedDevices(prev => prev.filter(id => !ids.includes(id)));
        } else {
            setSelectedDevices(prev => [...new Set([...prev, ...ids])]);
        }
    };

    const fetchComparison = () => {
        if (selectedDevices.length === 0) return;
        setLoading(true);
        axiosInstance.post('production_comparison/', {
            device_ids: selectedDevices.map(Number),
            period,
            shift_id: (period !== 'by_shift' && selectedShift) ? selectedShift : null,
        }).then(res => {
            const {labels, series: s} = res.data;
            const good = s.filter(d => d.role === 'good' || !d.role);
            const bad = s.filter(d => d.role === 'bad');
            const data = labels.map((lbl, i) => {
                const point = {label: lbl};
                good.forEach(dev => { point[dev.device_name] = dev.data[i] || 0; });
                bad.forEach(dev => { point['bad_' + dev.device_name] = dev.data[i] || 0; });
                return point;
            });
            setChartData(data);
            setGoodSeries(good);
            setBadSeries(bad);
        }).finally(() => setLoading(false));
    };

    const periodOptions = [
        {id: '24h', label: t('production.periodOptions.24h')},
        {id: '7d', label: t('production.periodOptions.7d')},
        {id: '30d', label: t('production.periodOptions.30d')},
        {id: 'monthly', label: t('common.monthly')},
        {id: 'yearly', label: t('common.yearly')},
        ...(shifts.length > 0 ? [{id: 'by_shift', label: t('common.byShift')}] : []),
    ];

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={9}/>
            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={t('production.title')} back={'/resources'}/>

                <div style={{display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '16px'}}>
                    {/* Left panel: controls */}
                    <div className={styles.comparisonFilters} style={{
                        backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #E2E8F0',
                        padding: '16px',
                    }}>
                        {/* Search */}
                        <input
                            type="text"
                            placeholder={t('production.search')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{
                                width: '100%', padding: '6px 10px', borderRadius: '6px',
                                border: '1px solid #E2E8F0', fontSize: '12px', outline: 'none',
                                marginBottom: '10px', boxSizing: 'border-box', color: '#334155',
                            }}
                        />

                        {/* Machines & Devices grouped */}
                        <div style={{fontWeight: 600, fontSize: '14px', color: '#334155', marginBottom: '8px'}}>{t('production.selectDevices')}</div>
                        <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                            {filteredGroups.map(group => {
                                const allSelected = group.devices.every(d => selectedDevices.includes(d.id));
                                const someSelected = group.devices.some(d => selectedDevices.includes(d.id));
                                const isExpanded = !!expandedMachines[group.id];
                                return (
                                    <div key={group.id} style={{marginBottom: '4px'}}>
                                        {/* Machine header */}
                                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 2px', cursor: 'pointer'}}
                                             onClick={() => toggleMachine(group.id)}>
                                            <FontAwesomeIcon
                                                icon={isExpanded ? faChevronDown : faChevronRight}
                                                style={{width: '10px', color: '#94A3B8', flexShrink: 0}}
                                            />
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                                onChange={e => { e.stopPropagation(); toggleAllInMachine(group.devices); }}
                                                onClick={e => e.stopPropagation()}
                                                style={{width: '14px', height: '14px', accentColor: '#4F46E5', flexShrink: 0}}
                                            />
                                            <span style={{fontSize: '13px', fontWeight: 600, color: '#334155'}}>{group.name || t('production.noMachine')}</span>
                                            <span style={{fontSize: '11px', color: '#94A3B8', marginLeft: 'auto'}}>
                                                {someSelected ? `${group.devices.filter(d => selectedDevices.includes(d.id)).length}/` : ''}{group.devices.length}
                                            </span>
                                        </div>
                                        {/* Devices */}
                                        {isExpanded && group.devices.map(d => (
                                            <label key={d.id} style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px 4px 26px', cursor: 'pointer'}}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDevices.includes(d.id)}
                                                    onChange={() => toggleDevice(d.id)}
                                                    style={{width: '13px', height: '13px', accentColor: '#4F46E5'}}
                                                />
                                                <span style={{fontSize: '12px', color: '#475569'}}>{d.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Period */}
                        <div style={{borderTop: '1px solid #F1F5F9', marginTop: '12px', paddingTop: '12px'}}>
                            <div style={{fontWeight: 600, fontSize: '14px', color: '#334155', marginBottom: '8px'}}>{t('production.period')}</div>
                            {periodOptions.map(p => (
                                <label key={p.id} style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px', cursor: 'pointer'}}>
                                    <input type="radio" name="period" value={p.id} checked={period === p.id}
                                        onChange={() => { setPeriod(p.id); if (p.id !== 'by_shift') setSelectedShift(null); }}
                                        style={{accentColor: '#4F46E5'}}/>
                                    <span style={{fontSize: '13px'}}>{p.label}</span>
                                </label>
                            ))}
                        </div>

                        {/* Shift filter — only when period is NOT by_shift and shifts exist */}
                        {shifts.length > 0 && period !== 'by_shift' && (
                            <div style={{borderTop: '1px solid #F1F5F9', marginTop: '12px', paddingTop: '12px'}}>
                                <div style={{fontWeight: 600, fontSize: '14px', color: '#334155', marginBottom: '8px'}}>{t('production.shift')}</div>
                                <label style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px', cursor: 'pointer'}}>
                                    <input type="radio" name="shift" value="" checked={!selectedShift} onChange={() => setSelectedShift(null)} style={{accentColor: '#4F46E5'}}/>
                                    <span style={{fontSize: '13px'}}>{t('production.allShifts')}</span>
                                </label>
                                {shifts.map(s => (
                                    <label key={s.id} style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px', cursor: 'pointer'}}>
                                        <input type="radio" name="shift" value={s.id} checked={selectedShift === s.id} onChange={() => setSelectedShift(s.id)} style={{accentColor: '#4F46E5'}}/>
                                        <div style={{width: '10px', height: '10px', borderRadius: '2px', backgroundColor: s.color, flexShrink: 0}}/>
                                        <span style={{fontSize: '13px'}}>{s.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}

                        {/* Chart type */}
                        <div style={{borderTop: '1px solid #F1F5F9', marginTop: '12px', paddingTop: '12px'}}>
                            <div style={{fontWeight: 600, fontSize: '14px', color: '#334155', marginBottom: '8px'}}>{t('production.chartType')}</div>
                            <div style={{display: 'flex', gap: '6px'}}>
                                {[{id:'bar', label:t('production.bar')}, {id:'line', label:t('production.line')}].map(ct => (
                                    <button key={ct.id} onClick={() => setChartType(ct.id)} style={{
                                        flex: 1, padding: '5px', borderRadius: '6px', border: '1px solid #E2E8F0',
                                        backgroundColor: chartType === ct.id ? '#4F46E5' : '#F8FAFC',
                                        color: chartType === ct.id ? '#fff' : '#64748B',
                                        fontSize: '12px', cursor: 'pointer', fontWeight: 500,
                                    }}>{ct.label}</button>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={fetchComparison}
                            disabled={loading || selectedDevices.length === 0}
                            style={{
                                marginTop: '16px', width: '100%', padding: '8px',
                                backgroundColor: '#4F46E5', color: '#fff', border: 'none',
                                borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                                cursor: selectedDevices.length === 0 ? 'not-allowed' : 'pointer',
                                opacity: selectedDevices.length === 0 ? 0.5 : 1,
                            }}
                        >
                            {loading ? t('common.loading') : t('common.confirm')}
                        </button>
                    </div>

                    {/* Right panel: charts */}
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '300px'}}>
                        {/* Good production chart */}
                        <div style={{backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #E2E8F0', padding: '20px'}}>
                            <div style={{fontWeight: 600, fontSize: '14px', color: '#334155', marginBottom: '12px'}}>
                                {t('production.goodProduction') || 'Good Production'}
                            </div>
                            {chartData.length === 0 ? (
                                <div style={{height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', fontSize: '14px'}}>
                                    {t('production.noData')}
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    {chartType === 'bar' ? (
                                        <BarChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                                            <XAxis dataKey="label" tick={{fontSize: 11}}/>
                                            <YAxis tick={{fontSize: 11}}/>
                                            <Tooltip/>
                                            <Legend/>
                                            {goodSeries.map((s, i) => (
                                                <Bar key={s.device_id} dataKey={s.device_name} fill={COLORS[i % COLORS.length]} radius={[4,4,0,0]}/>
                                            ))}
                                        </BarChart>
                                    ) : (
                                        <LineChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                                            <XAxis dataKey="label" tick={{fontSize: 11}}/>
                                            <YAxis tick={{fontSize: 11}}/>
                                            <Tooltip/>
                                            <Legend/>
                                            {goodSeries.map((s, i) => (
                                                <Line key={s.device_id} type="monotone" dataKey={s.device_name}
                                                    stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false}/>
                                            ))}
                                        </LineChart>
                                    )}
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Bad production chart — only shown when data exists */}
                        {badSeries.length > 0 && chartData.length > 0 && (
                            <div style={{backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #E2E8F0', padding: '20px'}}>
                                <div style={{fontWeight: 600, fontSize: '14px', color: '#334155', marginBottom: '12px'}}>
                                    {t('production.badProduction') || 'Bad Production'}
                                </div>
                                <ResponsiveContainer width="100%" height={300}>
                                    {chartType === 'bar' ? (
                                        <BarChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                                            <XAxis dataKey="label" tick={{fontSize: 11}}/>
                                            <YAxis tick={{fontSize: 11}}/>
                                            <Tooltip/>
                                            <Legend/>
                                            {badSeries.map((s, i) => (
                                                <Bar key={s.device_id} dataKey={'bad_' + s.device_name} name={s.device_name} fill={COLORS[(i + 4) % COLORS.length]} radius={[4,4,0,0]}/>
                                            ))}
                                        </BarChart>
                                    ) : (
                                        <LineChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                                            <XAxis dataKey="label" tick={{fontSize: 11}}/>
                                            <YAxis tick={{fontSize: 11}}/>
                                            <Tooltip/>
                                            <Legend/>
                                            {badSeries.map((s, i) => (
                                                <Line key={s.device_id} type="monotone" dataKey={'bad_' + s.device_name} name={s.device_name}
                                                    stroke={COLORS[(i + 4) % COLORS.length]} strokeWidth={2} dot={false}/>
                                            ))}
                                        </LineChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
